'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faFire } from '@fortawesome/pro-solid-svg-icons';
import { useLogging } from 'src/context/logging';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectDiscoveryMapHeatMap,
  setHeatMap,
  setSelectedHeatmapMetric,
} from 'src/redux/slices/discoveryMapSlice';
import { useIsAdvancedSearchActive } from 'src/hooks/discovery';
import { Button } from 'src/components';
import { HeatmapMetricPopover } from './components/HeatmapMetricPopover';

const HeatmapButton = ({
  className,
  isOldDiscovery,
}: {
  className?: string;
  isOldDiscovery?: boolean;
}) => {
  const log = useLogging();
  const dispatch = useDispatch();
  const heatMap = useSelector(selectDiscoveryMapHeatMap);
  const isAdvancedSearch = useIsAdvancedSearchActive();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(() => setPopoverOpen(false), []);
  const [wasAdvancedSearch, setWasAdvancedSearch] = useState(isAdvancedSearch);

  // Clear heatmap metric and turn off heatmap when leaving advanced search
  useEffect(() => {
    if (wasAdvancedSearch && !isAdvancedSearch) {
      dispatch(setHeatMap(false));
      dispatch(setSelectedHeatmapMetric(null));
      setPopoverOpen(false);
    }
    if (wasAdvancedSearch !== isAdvancedSearch) {
      setWasAdvancedSearch(isAdvancedSearch);
    }
  }, [isAdvancedSearch, wasAdvancedSearch, dispatch]);

  const handleClick = () => {
    if (isAdvancedSearch && !isOldDiscovery) {
      setPopoverOpen((prev) => !prev);
      log.event('Heatmap Popover', { opened: !popoverOpen });
    } else {
      dispatch(setHeatMap(!heatMap));
      log.event('Heatmap', { enabled: !heatMap });
    }
  };

  if (isOldDiscovery) {
    return (
      <Button
        id="discovery-territory-heatmap-button"
        variant="soft"
        color="neutral"
        onClick={handleClick}
        className={`
          flex items-center justify-center h-9 w-9 !rounded-r-lg !rounded-l-none overflow-hidden shadow-lg border border-neutral-200 !text-xl
          ${className}
          ${
            heatMap
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-white text-neutral-600 hover:bg-gray-100'
          }
        `}
      >
        <Icon icon={faFire} className="w-4 h-4" />
      </Button>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        id="discovery-territory-heatmap-button"
        data-testid="discovery-territory-heatmap-button"
        className={`
          p-4 rounded-lg shadow-[0px_8px_8px_0px_rgba(0,0,0,0.08)] outline outline-1 -outline-offset-1 outline-neutral-300 flex flex-col justify-center items-center gap-4 cursor-pointer overflow-hidden
          ${
            heatMap
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-white text-neutral-600 hover:bg-neutral-100'
          }
        `}
        onClick={handleClick}
        aria-label="Heatmap"
        aria-haspopup={isAdvancedSearch ? 'listbox' : undefined}
        aria-expanded={isAdvancedSearch ? popoverOpen : undefined}
      >
        <Icon icon={faFire} className="w-4 h-4" />
      </button>
      {isAdvancedSearch && (
        <HeatmapMetricPopover
          isOpen={popoverOpen}
          onClose={handleClose}
          anchorRef={buttonRef}
        />
      )}
    </>
  );
};

export default HeatmapButton;
